import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import AxiosInstance from '../AxiosInstance';
import config from '../config/frontend.config';

// Global flags to prevent duplicate notification calls
let globalNotificationFetching = false;
let globalUnreadCountFetching = false;

const NotificationContext = createContext();

const BASE_URL = config.api.baseURL + '/' + config.api.apiPath;

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const hasInitialized = useRef(false);
  const isInitializing = useRef(false);
  const isMounted = useRef(true);
  const pollingIntervalRef = useRef(null);

  const userId = config.getUserId();

  // Fetch notifications for a specific user
  const fetchNotifications = useCallback(async () => {
    if (!userId || !config.isAuthenticated()) {
      return;
    }

    // Global check to prevent duplicate calls
    if (globalNotificationFetching) {
      return;
    }

    try {
      globalNotificationFetching = true;
      setLoading(true);
      setError(null);
      
      const apiUrl = `${BASE_URL}/notifications/user/${userId}`;
      
      const response = await AxiosInstance.get(apiUrl, {
        params: {
          limit: 50,
          page: 1
        }
      });

      if (response.data.statusCode === 200) {
        setNotifications(response.data.data || []);
      }
    } catch (err) {
      console.error('Error fetching notifications:', err);
      setError(err.response?.data?.message || 'Failed to fetch notifications');
    } finally {
      setLoading(false);
      globalNotificationFetching = false; // Reset global flag
    }
  }, [userId, BASE_URL]);

  // Fetch unread count
  const fetchUnreadCount = useCallback(async () => {
    if (!userId || !config.isAuthenticated()) {
      return;
    }

    // Global check to prevent duplicate calls
    if (globalUnreadCountFetching) {
      return;
    }

    try {
      globalUnreadCountFetching = true;
      const apiUrl = `${BASE_URL}/notifications/unread-count/${userId}`;
      
      const response = await AxiosInstance.get(apiUrl);
      
      if (response.data.statusCode === 200) {
        setUnreadCount(response.data.unreadCount || 0);
      }
    } catch (err) {
      console.error('Error fetching unread count:', err);
    } finally {
      globalUnreadCountFetching = false; // Reset global flag
    }
  }, [userId, BASE_URL]);

  // Mark notification as read
  const markAsRead = useCallback(async (notificationId) => {
    try {
      const response = await AxiosInstance.put(`${BASE_URL}/notifications/${notificationId}`);
      
      
      if (response.data.statusCode === 200) {
        // Update local state
        setNotifications(prev => 
          prev.map(notification => 
            notification.NotificationId === notificationId 
              ? { ...notification, IsView: true }
              : notification
          )
        );
        
        // Update unread count
        setUnreadCount(prev => Math.max(0, prev - 1));
        
        return true;
      }
    } catch (err) {
      console.error('Error marking notification as read:', err);
      return false;
    }
  }, []);

  // Mark all notifications as read
  const markAllAsRead = useCallback(async () => {
    if (!userId) return;

    try {
      const response = await AxiosInstance.put(`${BASE_URL}/notifications/view-all/${userId}`);
      
      if (response.data.statusCode === 200) {
        // Update local state
        setNotifications(prev => 
          prev.map(notification => ({ ...notification, IsView: true }))
        );
        
        // Reset unread count
        setUnreadCount(0);
        
        return true;
      }
    } catch (err) {
      console.error('Error marking all notifications as read:', err);
      return false;
    }
  }, [userId, BASE_URL]);

  // Delete notification (soft delete)
  const deleteNotification = useCallback(async (notificationId) => {
    try {
      
      // Call the delete API
      const response = await AxiosInstance.delete(`${BASE_URL}/notifications/${notificationId}`);
      
      
      if (response.data.statusCode === 200) {
        // Update local state
        setNotifications(prev => 
          prev.filter(notification => notification.NotificationId !== notificationId)
        );
        
        // If it was unread, decrease count
        const notification = notifications.find(n => n.NotificationId === notificationId);
        if (notification && !notification.IsView) {
          setUnreadCount(prev => Math.max(0, prev - 1));
        }
        
        return true;
      }
    } catch (err) {
      console.error('Error deleting notification:', err);
      console.error('Error details:', err.response?.data);
      return false;
    }
  }, [notifications, BASE_URL]);

  // Manual refresh function for immediate updates
  const refreshNotifications = useCallback(async () => {
    await Promise.all([
      fetchNotifications(),
      fetchUnreadCount()
    ]);
  }, [fetchNotifications, fetchUnreadCount]);

  // Initial fetch - only run once when component mounts
  useEffect(() => {
    if (!userId || !config.isAuthenticated()) {
      return;
    }
    
    // Prevent multiple initializations
    if (hasInitialized.current || isInitializing.current) {
      return;
    }
    
    isInitializing.current = true;
    hasInitialized.current = true;
    
    // Execute both calls
    fetchNotifications();
    fetchUnreadCount();
    
    // Cleanup function to reset initialization when userId changes
    return () => {
      hasInitialized.current = false;
      isInitializing.current = false;
      isMounted.current = false;
      
      // Clear polling interval if it exists
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [userId]); // Only depend on userId, not the functions

  // Set up polling for new notifications (DISABLED to reduce API calls)
  useEffect(() => {
    // Polling is disabled to reduce API calls
    // If you want to enable polling, change this to true
    const isPollingEnabled = false;
    
    if (!userId || !config.isAuthenticated() || !isPollingEnabled) return;
    
    // Clear any existing interval
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }
    
    pollingIntervalRef.current = setInterval(async () => {
      // Only poll if component is still mounted and user is still authenticated
      if (!isMounted.current || !config.isAuthenticated()) {
        return;
      }
      
      try {
        const apiUrl = `${BASE_URL}/notifications/unread-count/${userId}`;
        const response = await AxiosInstance.get(apiUrl);
        
        if (response.data.statusCode === 200) {
          setUnreadCount(response.data.unreadCount || 0);
        }
      } catch (err) {
        console.error('Error in polling unread count:', err);
      }
    }, 300000); // 5 minutes

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [userId, BASE_URL]);

  const value = {
    notifications,
    unreadCount,
    loading,
    error,
    fetchNotifications,
    fetchUnreadCount,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    refreshNotifications
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
}; 